import noop from 'lodash/noop';
import {
  createContext,
  PropsWithChildren,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { decodeSearchParams } from '@pages/CaseDashboard/utils';
import { SortDirectionValues } from './utils';

export enum DeveloperToolsTabs {
  API_KEYS = 'api-keys',
  WEBHOOKS = 'webhooks',
}

export type DeveloperToolsFilters = {
  environment?: string;
  status?: string;
  sortOrder?: SortDirectionValues;
  sortField?: string;
};

interface DeveloperToolsContextProps {
  selectedTab: DeveloperToolsTabs;
  setSelectedTab: (tab: DeveloperToolsTabs) => void;
  filters: DeveloperToolsFilters;
}

const defaultFilters = {
  environment: '',
  status: '',
  sortOrder: 'desc' as SortDirectionValues,
  sortField: 'createdAt',
};

const DeveloperToolsContext = createContext<DeveloperToolsContextProps>({
  selectedTab: DeveloperToolsTabs.API_KEYS,
  setSelectedTab: noop,
  filters: defaultFilters,
});

export function DeveloperToolsProvider({ children }: PropsWithChildren) {
  const [searchParams] = useSearchParams();
  const [selectedTab, setSelectedTab] = useState<DeveloperToolsTabs>(
    DeveloperToolsTabs.API_KEYS,
  );

  const value = useMemo(() => {
    const filters = { ...defaultFilters, ...decodeSearchParams(searchParams) };
    return {
      selectedTab,
      setSelectedTab,
      filters,
    };
  }, [selectedTab, searchParams]);

  return (
    <DeveloperToolsContext.Provider value={value}>
      {children}
    </DeveloperToolsContext.Provider>
  );
}

export const useDeveloperTools = (): DeveloperToolsContextProps => {
  const context = useContext(DeveloperToolsContext);
  if (!context) {
    throw new Error(
      'useDeveloperTools must be used within a DeveloperToolsProvider',
    );
  }
  return context;
};
